import React, { useState, useEffect } from 'react';
import { Trophy } from 'lucide-react';
import { useApiCaller } from '../utils/ApiUtilities';
import { getAdminGenerateCompetitionUrl } from '../utils/UrlUtilities';
import { constructGenerateCompetitionPayload } from '../utils/PayloadUtils';
import CloseButton from './ui/CloseButton';

const PURPLE_GRADIENT = 'linear-gradient(145deg,#7c3aed,#5b21b6)';

// Generates a competition quiz by merging one random published Reading micro + one Listening
// micro at the level. Created unpublished; admin publishes later (which starts the N-hour window).
const GenerateCompetitionModal = ({ level = 'TOEFL', onClose, onGenerated }) => {
    const CallApi = useApiCaller();
    const [openHours, setOpenHours] = useState(24);
    const [readingMinutes, setReadingMinutes] = useState('');
    const [listeningMinutes, setListeningMinutes] = useState('');
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, []);

    const canGenerate = Number(openHours) >= 1 && !generating;

    const handleGenerate = async () => {
        setError('');
        setGenerating(true);
        try {
            const payload = constructGenerateCompetitionPayload(level, openHours, readingMinutes, listeningMinutes);
            const res = await CallApi({ url: getAdminGenerateCompetitionUrl(), method: 'POST', payload, token: true });
            if (res.success) {
                // Hand the result to the parent, which closes this modal and shows the result modal.
                if (onGenerated) onGenerated(res.data);
            } else {
                setError(res.message || 'Failed to generate competition test.');
            }
        } catch (err) {
            console.error('Error generating competition quiz:', err);
            setError('An error occurred while generating the competition test.');
        } finally {
            setGenerating(false);
        }
    };

    return (
        <div style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '1rem',
        }}>
            <div style={{
                backgroundColor: '#ffffff', borderRadius: '0.9rem',
                boxShadow: '0 10px 25px -5px rgba(0,0,0,0.3)', width: '90%', maxWidth: 'min(560px, 96vw)',
                display: 'flex', flexDirection: 'column', overflow: 'hidden',
            }}>
                <div style={{ background: PURPLE_GRADIENT, color: '#fff', padding: '1.1rem 1.25rem', position: 'relative' }}>
                    <CloseButton onClick={onClose} />
                    <h2 style={{ fontSize: '1.3rem', fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Trophy style={{ width: '1.25rem', height: '1.25rem' }} /> Generate Competition Test — {level}
                    </h2>
                    <p style={{ margin: '0.35rem 0 0', opacity: 0.85, fontSize: '0.85rem' }}>
                        Merges one random published Reading micro + one Listening micro into a single competition
                        test (created unpublished). Publishing it starts the open window.
                    </p>
                </div>

                <div style={{ padding: '1.25rem' }}>
                    {error && <p style={{ color: '#dc2626' }}>{error}</p>}
                    <label className="form-label fw-semibold">Open for (hours after publish)</label>
                    <input
                        type="number"
                        min="1"
                        className="form-control"
                        value={openHours}
                        onChange={e => setOpenHours(e.target.value)}
                        style={{ maxWidth: 200 }}
                    />
                    <small className="form-text text-muted d-block mt-1">
                        Users may start only within this window after publish. Each attempt is timed per section.
                    </small>

                    <div className="row g-2 mt-2">
                        <div className="col-6">
                            <label className="form-label fw-semibold">Reading minutes <span className="text-muted small">(optional)</span></label>
                            <input type="number" min="1" className="form-control" value={readingMinutes}
                                onChange={e => setReadingMinutes(e.target.value)} placeholder="from base test" />
                        </div>
                        <div className="col-6">
                            <label className="form-label fw-semibold">Listening minutes <span className="text-muted small">(optional)</span></label>
                            <input type="number" min="1" className="form-control" value={listeningMinutes}
                                onChange={e => setListeningMinutes(e.target.value)} placeholder="from base test" />
                        </div>
                        <div className="col-12"><small className="form-text text-muted">Leave blank to inherit each section's time from the source model test.</small></div>
                    </div>
                </div>

                <div style={{ borderTop: '1px solid #e5e7eb', padding: '0.85rem 1.25rem', display: 'flex', justifyContent: 'flex-end', gap: '0.6rem' }}>
                    <button className="btn btn-light" onClick={onClose}>Close</button>
                    <button className="btn btn-primary" disabled={!canGenerate} onClick={handleGenerate}>
                        {generating ? 'Generating…' : 'Generate Competition Test'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default GenerateCompetitionModal;
